import { ToolLabel } from '@/app/technical';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { WireAttachedTool } from '@/api/wire';
import { Badge } from './primitives';
import { when } from './records';
import { ExplainedRow } from '@/oversight/ExplainedRow';
import { explainAttachedTool } from './tools-explain';

/**
 * A tool's use against its ceiling, and what the agents of a page may use
 * (proposal 0009, SG-09, ADR-0134). Both read what the gateway enforces;
 * neither decides anything.
 */

/**
 * Today's calls against the manifest's daily ceiling. At the ceiling the
 * gateway refuses with `rate_limited` until the day turns, so the meter
 * says when it resumes rather than only that it is full. A manifest that
 * states no daily ceiling is said to, not drawn as an empty bar.
 */
export function CeilingMeter({ used, ceiling, refused = 0, resumesAt }: { used: number; ceiling: number | null; refused?: number; resumesAt?: string | null }) {
  if (ceiling === null) {
    return (
      <span className="text-dense text-secondary" data-testid="ceiling-meter">
        {used} today · no daily ceiling in its manifest
      </span>
    );
  }
  const spent = used >= ceiling;
  const pct = ceiling > 0 ? Math.min(100, (used / ceiling) * 100) : 100;
  return (
    <span className="flex min-w-[140px] flex-col gap-1" data-testid="ceiling-meter">
      <span className="flex items-baseline justify-between gap-2 text-dense tabular-nums">
        <span className={spent ? 'font-semibold text-danger' : ''}>
          {used} / {ceiling} today
        </span>
        {refused > 0 && <span className="text-label text-danger">{refused} refused</span>}
      </span>
      <span
        role="meter"
        aria-label="Calls today against the daily ceiling"
        aria-valuemin={0}
        aria-valuemax={ceiling}
        aria-valuenow={Math.min(used, ceiling)}
        className="relative block h-1.5 bg-sunken"
      >
        <span className={`absolute inset-y-0 left-0 block ${spent ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-accent'}`} style={{ width: `${pct}%` }} />
      </span>
      {spent && <span className="text-label text-meta">At its ceiling · refused until {resumesAt ? when(resumesAt) : 'the day turns (UTC)'}</span>}
    </span>
  );
}

const ORIGIN: Record<WireAttachedTool['origin'], string> = {
  core: 'Google core',
  own: 'Ours',
  third_party: 'Third party',
};

/**
 * What agents may use here: every tool the department's agents hold, at
 * the level it is attached (the department's standing assignments, or a
 * plan's), with who holds it, today's use and refusals, and when it ends.
 * A vendor's tool the superadmin has not admitted is refused whatever is
 * attached (ADR-0133), and the strip says so on its row.
 */
export interface StripEconomics {
  replaceRank: number | null;
  replaceDue: boolean | null;
  callsMonth: number;
}

export function PermissionsStrip({
  tools,
  department,
  countedFrom,
  agentName = (id) => id,
  action,
  economics,
}: {
  tools: WireAttachedTool[];
  department: string;
  countedFrom?: string | undefined;
  agentName?: (id: string) => string;
  /** The one thing to do: attach a tool, or where to ask for one. */
  action?: ReactNode;
  /** Each tool's place in the replace order (ADR-0133), from the Tool library. */
  economics?: Map<string, StripEconomics> | undefined;
}) {
  return (
    <section className="flex flex-col border border-strong" aria-label="Tools and permissions on this page" data-testid="permissions-strip">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-line bg-raised px-4 py-3">
        <span className="text-micro font-semibold uppercase tracking-label">What agents may use here</span>
        <span className="min-w-[240px] flex-1 text-label text-meta">
          {department}’s agents, as the gateway decides each call. Agents see this page by its ids, never its text.
          {countedFrom ? ` Counted from ${countedFrom}.` : ''}
        </span>
        {action}
      </div>
      {tools.length === 0 ? (
        <p className="m-0 px-4 py-3.5 text-dense text-secondary">
          No tool is attached here. An agent on this page can read the records by id and nothing else — it cannot search Maps, LinkedIn or the web from here.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr>
                {['Tool', 'Attached to', 'Agents', 'Used today', 'Refused today', 'Until'].map((h) => (
                  <th key={h} scope="col" className="border-b border-line px-4 py-2 text-left text-micro font-medium uppercase tracking-label text-meta">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tools.map((t) => (
                <ExplainedRow
                  key={`${t.toolId}:${t.level}:${t.plan?.id ?? ''}`}
                  testId="attached-tool"
                  colSpan={6}
                  leadClassName="px-4 py-2.5 align-top"
                  context={explainAttachedTool(t, department, agentName)}
                  lead={
                    <span className="flex flex-col gap-1">
                      <span className="text-dense font-semibold"><ToolLabel id={t.toolId} /></span>
                      <span className="flex flex-wrap items-center gap-1.5 text-label text-meta">
                        {ORIGIN[t.origin]}
                        {t.origin === 'third_party' && !t.admitted && <Badge tone="danger">NOT ADMITTED · REFUSED</Badge>}
                        {(() => {
                          const e = economics?.get(t.toolId);
                          if (!e) return null;
                          return (
                            <>
                              {e.replaceRank !== null && <span>Replace #{e.replaceRank}</span>}
                              {e.replaceDue && <Badge tone="warning">Replacement due</Badge>}
                              <span>{e.callsMonth} calls, 30 days</span>
                            </>
                          );
                        })()}
                      </span>
                    </span>
                  }
                >
                  <td className="px-4 py-2.5 align-top text-dense text-secondary">
                    {t.level === 'plan' ? (
                      t.plan ? (
                        <Link className="text-accent underline" to={`/prospecting/plans/${t.plan.id}`}>
                          The plan · {t.plan.name}
                        </Link>
                      ) : (
                        'A plan'
                      )
                    ) : (
                      `The department · ${department}`
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top text-dense">{t.agents.map(agentName).join(', ') || '—'}</td>
                  <td className="px-4 py-2.5 align-top">
                    <CeilingMeter used={t.usedToday} ceiling={t.ceilingPerDay} />
                  </td>
                  <td className={`px-4 py-2.5 align-top text-dense tabular-nums ${t.refusedToday > 0 ? 'text-danger' : 'text-meta'}`}>{t.refusedToday}</td>
                  <td className="px-4 py-2.5 align-top text-dense text-secondary">{t.until ? when(t.until) : 'Until it is ended'}</td>
                </ExplainedRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
